import React, { useEffect, useState } from "react";
import useAuthStore from "../../../store/useAuthStore";
import useCompanyStore from "../../../store/useCompanyStore";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Alert, Snackbar } from "@mui/material";


const EmployeePayslips = () => {
  const { token, user } = useAuthStore();
  const { companyInfo } = useCompanyStore();
  const companyName = companyInfo?.name || "Company";
  const employeeId = user?.id;
  const empId = user?.employeeId;
  const [payslips, setPayslips] = useState([]);
  const [allPayslips, setAllPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    // Default to previous month
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const defaultMonth = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, "0")}`;
    setSelectedMonth(defaultMonth);
    fetchEmployeePayslips(defaultMonth);
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchEmployeePayslips = async (initialMonth) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/employee-payslips/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setSnackbar({ open: true, message: "Payslips fetched successfully!", severity: "success" });
        setAllPayslips(data.payslips);
        // Apply initial month filter directly with fetched data to avoid stale state
        filterPayslipsByMonth(initialMonth || selectedMonth, data.payslips);
      } else {
        setError(data.error || "Failed to fetch payslips");
      }
    } catch (err) {
      setError("Server error");
    }
    setLoading(false);
  };

  const filterPayslipsByMonth = (monthVal, payslipList) => {
    const list = payslipList || allPayslips;
    if (!monthVal) {
      setPayslips(list);
      return;
    }
    const [year, month] = monthVal.split("-").map(Number);
    const filtered = list.filter((p) => {
      const d = new Date(p.startDate);
      return d.getFullYear() === year && d.getMonth() + 1 === month;
    });
    setPayslips(filtered);
  };

  const handleMonthChange = (e) => {
    const val = e.target.value;
    setSelectedMonth(val);
    filterPayslipsByMonth(val, allPayslips);
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Download function (Modern Professional Template)
  const handleDownload = (payslipData) => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageW = 595;
    const fmt = (amt) => `Nu. ${formatCurrency(amt)}`;

    const basicSalary = Number(payslipData.basicSalary) || 0;
    const allowances = payslipData.allowances || [];
    const deductions = payslipData.deductions || [];
    const allowancesTotal = allowances.reduce((s, a) => s + (Number(a.amount) || 0), 0);
    const grossSalary = basicSalary + allowancesTotal;
    const totalDeductions = deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const netPay = grossSalary - totalDeductions;
    const taxableSalary = payslipData.taxableSalary || Math.round(grossSalary * 0.85);
    const tds = payslipData.tds ?? 0;

    // ── Header ──
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, pageW, 78, "F");
    doc.setFillColor(147, 197, 253);
    doc.rect(0, 78, pageW, 4, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text(companyName.toUpperCase(), 40, 36);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("SALARY SLIP", 40, 56);

    const monthYear = new Date(payslipData.startDate).toLocaleString("default", { month: "long", year: "numeric" });
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(monthYear.toUpperCase(), pageW - 40, 36, { align: "right" });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Period: ${new Date(payslipData.startDate).toLocaleDateString()} – ${new Date(payslipData.endDate).toLocaleDateString()}`,
      pageW - 40, 56, { align: "right" }
    );

    // ── Employee Info Bar ──
    doc.setFillColor(239, 246, 255);
    doc.rect(0, 82, pageW, 72, "F");

    const empId2  = payslipData.empId || (typeof payslipData.employeeId === "object" ? payslipData.employeeId?.employeeId : null) || empId || "N/A";
    const dept2   = payslipData.department || (typeof payslipData.employeeId === "object" ? payslipData.employeeId?.department : null) || "N/A";
    const bank2   = payslipData.bankDetails?.accountNumber || payslipData.bankDetails?.account || "N/A";

    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "bold");
    doc.text("EMPLOYEE INFORMATION", 40, 99);

    const labelX = 40, valX = 130, labelX2 = 320, valX2 = 410;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);

    [
      ["Name", payslipData.name || "N/A", "Department", dept2, 115],
      ["Employee ID", empId2, "Bank / Cheque", bank2, 133],
    ].forEach(([l1, v1, l2, v2, y]) => {
      doc.setFont("helvetica", "bold"); doc.text(l1 + ":", labelX, y);
      doc.setFont("helvetica", "normal"); doc.text(String(v1), valX, y);
      doc.setFont("helvetica", "bold"); doc.text(l2 + ":", labelX2, y);
      doc.setFont("helvetica", "normal"); doc.text(String(v2), valX2, y);
    });

    // ── Earnings & Deductions Combined Table ──
    const earningsRows = [
      ["Basic Salary", fmt(basicSalary)],
      ...allowances.map((a) => [a.name, fmt(a.amount)]),
    ];
    const deductionRows = deductions.map((d) => [d.name === "TDS" ? "TDS (Income Tax)" : d.name, fmt(d.amount)]);

    const maxLen = Math.max(earningsRows.length, deductionRows.length);
    while (earningsRows.length < maxLen) earningsRows.push(["", ""]);
    while (deductionRows.length < maxLen) deductionRows.push(["", ""]);

    const tableBody = earningsRows.map((e, i) => [e[0], e[1], deductionRows[i][0], deductionRows[i][1]]);
    tableBody.push(["Gross Salary", fmt(grossSalary), "Total Deductions", fmt(totalDeductions)]);

    doc.autoTable({
      startY: 170,
      head: [["EARNINGS", "AMOUNT", "DEDUCTIONS", "AMOUNT"]],
      body: tableBody,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: { top: 5, bottom: 5, left: 6, right: 6 }, lineColor: [226, 232, 240], lineWidth: 0.4 },
      headStyles: { fillColor: [96, 165, 250], textColor: 255, fontStyle: "bold", halign: "center", fontSize: 9 },
      columnStyles: {
        0: { halign: "left", cellWidth: 148 },
        1: { halign: "right", cellWidth: 110, textColor: [37, 99, 235] },
        2: { halign: "left", cellWidth: 148 },
        3: { halign: "right", cellWidth: 110, textColor: [220, 38, 38] },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (data) => {
        if (data.row.index === tableBody.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = data.column.index < 2 ? [220, 252, 231] : [255, 237, 237];
          data.cell.styles.textColor = data.column.index < 2 ? [22, 163, 74] : [220, 38, 38];
          data.cell.styles.lineColor = [200, 210, 220];
        }
      },
      margin: { left: 27, right: 27 },
    });

    // ── Tax Summary ──
    const taxY = doc.lastAutoTable.finalY + 12;
    doc.autoTable({
      startY: taxY,
      head: [["Taxable Salary (85%)", "TDS / Income Tax"]],
      body: [[fmt(taxableSalary), fmt(tds)]],
      theme: "grid",
      styles: { fontSize: 9, halign: "center", cellPadding: 5, lineColor: [226, 232, 240], lineWidth: 0.4 },
      headStyles: { fillColor: [148, 163, 184], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 270 }, 1: { cellWidth: 270 } },
      margin: { left: 27, right: 27 },
    });

    // ── Net Pay Box ──
    const netY = doc.lastAutoTable.finalY + 14;
    doc.setFillColor(16, 185, 129);
    doc.roundedRect(27, netY, pageW - 54, 46, 4, 4, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text("NET PAY", 50, netY + 19);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Employee Net Pay", 50, netY + 33);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(fmt(netPay), pageW - 50, netY + 30, { align: "right" });

    // ── Footer ──
    doc.setDrawColor(226, 232, 240);
    doc.line(27, 795, pageW - 27, 795);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(148, 163, 184);
    doc.text("This is a computer-generated document. No signature is required.", 27, 808);
    doc.text(`Generated by ${companyName} | ${new Date().toLocaleDateString()}`, pageW - 27, 808, { align: "right" });

    doc.save(`payslip-${payslipData.name || "employee"}-${monthYear}.pdf`);
  };

  return (
    <>
      <div className="p-8 border ">
        <h2 className="text-2xl font-bold mb-4">Payslips for Employee</h2>

        {/* Month filter */}
        <div className="mb-6 flex flex-col md:flex-row items-center gap-4 border p-4 rounded-lg shadow-md bg-gray-50">
          <div>
            <label className="block text-sm font-medium mb-1">Filter by Month</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={handleMonthChange}
              className="border rounded px-3 py-2"
            />
          </div>
          <button
            type="button"
            onClick={() => { setSelectedMonth(""); setPayslips(allPayslips); }}
            className="bg-gray-400 text-white px-4 py-2 rounded mt-2 md:mt-6 text-sm"
          >
            Clear Filter
          </button>
        </div>

        {loading ? (
          <p>Loading payslips...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : payslips.length === 0 ? (
          <p>No payslips available for the selected period.</p>
        ) : (
          <ul className="space-y-4">
            {payslips.map((payslip) => {
              const totalAllowances = payslip.allowances?.reduce((sum, a) => sum + (a.amount || 0), 0);
              const totalDeductions = payslip.deductions?.reduce((sum, d) => sum + (d.amount || 0), 0);
              const grossPay = payslip.basicSalary + totalAllowances;
              const netPay = grossPay - totalDeductions;

              return (
                <li key={payslip._id} className="p-6 border rounded-xl shadow-md bg-white relative">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold">
                      PaySlip for period :{" "}
                      {new Date(payslip.startDate).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}{" "}
                      -{" "}
                      {new Date(payslip.endDate).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </h2>

                    <button
                      onClick={() => handleDownload(payslip)}
                      className="text-sm bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                      Download
                    </button>
                  </div>

                  <div className="flex flex-col md:flex-row gap-8">
                    {/* Employee Info */}
                    <div className="flex-1 bg-gray-50 p-4 rounded-lg border">
                      <h2 className="text-lg font-semibold mb-2">Employee Details</h2>
                      <div className="text-sm text-gray-600">
                        <div className="flex">
                          <span className="w-32 font-medium">Employee ID</span>
                          <span>: {empId || "N/A"}</span>
                        </div>
                        <div className="flex">
                          <span className="w-32 font-medium">Name</span>
                          <span>: {payslip.name || "N/A"}</span>
                        </div>
                        <div className="flex">
                          <span className="w-32 font-medium">Period</span>
                          <span>
                            : {new Date(payslip.startDate).toLocaleDateString()} -{" "}
                            {new Date(payslip.endDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex">
                          <span className="w-32 font-medium">Bank/Cheque</span>
                          <span>: {payslip.bankDetails?.accountNumber || "N/A"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Salary Summary */}
                    <div className="w-full md:w-1/2 bg-white p-4 rounded-lg shadow border">
                      <h2 className="text-lg font-semibold mb-2">Salary Summary</h2>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div className="flex justify-between">
                          <span>Basic Salary</span>
                          <span>Nu. {formatCurrency(payslip.basicSalary)}</span>
                        </div>
                        
                        {/* Detailed Allowances */}
                        {payslip?.allowances && payslip.allowances.length > 0 ? (
                          payslip.allowances.map((item, index) => (
                            <div key={`emp-allowance-${index}`} className="flex justify-between text-gray-600 pl-2">
                              <span>+ {item.name}</span>
                              <span>Nu. {formatCurrency(item.amount)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="flex justify-between">
                            <span>Total Allowances</span>
                            <span>Nu. {formatCurrency(totalAllowances)}</span>
                          </div>
                        )}

                        {/* Detailed Deductions */}
                        {payslip?.deductions && payslip.deductions.length > 0 ? (
                          payslip.deductions.map((item, index) => (
                            <div key={`emp-deduction-${index}`} className="flex justify-between text-gray-600 pl-2">
                              <span>- {item.name} {item.name === "TDS" ? "(Income Tax)" : ""}</span>
                              <span>Nu. {formatCurrency(item.amount)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="flex justify-between">
                            <span>Total Deductions</span>
                            <span>Nu. {formatCurrency(totalDeductions)}</span>
                          </div>
                        )}

                        <div className="flex justify-between text-gray-500 mt-2 border-t pt-2">
                          <span>Taxable Salary (85%)</span>
                          <span>Nu. {formatCurrency(payslip.taxableSalary ? Math.round(payslip.taxableSalary) : Math.round(grossPay * 0.85))}</span>
                        </div>
                        <hr className="my-8" />
                        <div className="flex gap-4 mt-4">
                          <div className="flex-1 bg-green-100 border-l-4 rounded-2xl border-green-500 text-2xl font-bold p-3 text-center">
                            <p className="text-gray-900">Gross Pay: Nu. {formatCurrency(grossPay)}</p>
                          </div>
                          <div className="flex-1 bg-green-100 border-r-4 rounded-2xl border-green-500 text-2xl font-bold p-3 text-center">
                            <p className="text-gray-900">Net Pay: Nu. {formatCurrency(netPay)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            color: "white",
            backgroundColor:
              snackbar.severity === "success" ? "#22c55e" : "#ef4444",
            "& .MuiAlert-icon": {
              color: "white",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmployeePayslips;